import React from 'react';
import { Info, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/Card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../ui/shadcn';
import { cn } from '../../../../utils';
import { getMetricDef, MetricId } from '../metricDefs';

interface ChartCardProps {
  metric: MetricId;
  isEmpty?: boolean;
  emptyMessage?: string;
  className?: string;
  /** Tailwind height for the plot area — taller for the lead chart. */
  heightClassName?: string;
  /** Small caption under the title, e.g. the bucket width in use. */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}

/** Flat chart shell with consistent height, tooltip and empty state. */
const ChartCard: React.FC<ChartCardProps> = ({
  metric,
  isEmpty = false,
  emptyMessage = 'No data for this period',
  className,
  heightClassName = 'h-52',
  subtitle,
  children,
}) => {
  const def = getMetricDef(metric);
  return (
    <Card className={cn('border-blue-100 bg-white', className)}>
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center gap-1.5">
          <CardTitle className="text-sm font-semibold text-blue-950">{def.label}</CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`What is ${def.label}?`}
                className="text-blue-400 hover:text-blue-700"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {def.description}
            </TooltipContent>
          </Tooltip>
        </div>
        {subtitle != null && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {isEmpty ? (
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-2 text-muted-foreground',
              heightClassName,
            )}
          >
            <BarChart3 className="h-8 w-8 text-blue-200" />
            <p className="text-sm">{emptyMessage}</p>
          </div>
        ) : (
          <div className={cn('w-full', heightClassName)}>{children}</div>
        )}
      </CardContent>
    </Card>
  );
};

export default ChartCard;
